import { Box, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { humanizeKey, formatArrayValue } from "@/pages/AlgoManagement/utils";

function formatValue(value: unknown): string {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return formatArrayValue(value);
    if (typeof value === "object") return "";
    if (typeof value === "string") {
        // Only treat as date if it looks like an ISO date or datetime (e.g. "2026-02-17" or "2026-02-17 17:56:17...")
        const looksLikeDate = /^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(value.trim());
        if (looksLikeDate) {
            const d = dayjs(value);
            if (d.isValid()) return d.format("MM/DD/YYYY - HH:mm:ss");
        }
        return value;
    }
    return String(value);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

interface KeyValueRowProps {
    label: string;
    value: string;
}

const KeyValueRow = ({ label, value }: KeyValueRowProps) => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 3,
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
                "&:last-of-type": { borderBottom: 0 },
            }}
        >
            <Typography
                variant="body2"
                sx={{ color: theme.palette.bg[800], flexShrink: 0, fontWeight: 500 }}
            >
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: theme.palette.bg[400],
                    textAlign: "right",
                    wordBreak: "break-word",
                    maxWidth: "60%",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

interface SectionProps {
    title: string;
    data: Record<string, unknown>;
}

const Section = ({ title, data }: SectionProps) => {
    const theme = useTheme();
    const entries = Object.entries(data).filter(
        ([_, v]) => v !== undefined && v !== null
    );
    if (entries.length === 0) return null;

    return (
        <Box sx={{ mb: 3 }}>
            <Typography
                variant="subtitle2"
                sx={{
                    color: (theme.palette.secondary as unknown as Record<string, string>)[400],
                    mb: 1.5,
                    fontWeight: 600,
                }}
            >
                {title}
            </Typography>
            <Box sx={{ pl: 1 }}>
                {entries.map(([key, value]) => {
                    if (isPlainObject(value) && Object.keys(value).length > 0) {
                        return (
                            <Section
                                key={key}
                                title={humanizeKey(key)}
                                data={value}
                            />
                        );
                    }
                    if (Array.isArray(value) && value.some(isPlainObject)) {
                        const numbered = Object.fromEntries(
                            value.map((item, i) => [String(i + 1), item])
                        );
                        return (
                            <Section
                                key={key}
                                title={humanizeKey(key)}
                                data={numbered}
                            />
                        );
                    }
                    const displayValue = formatValue(value);
                    if (displayValue === "") return null;
                    return (
                        <KeyValueRow
                            key={key}
                            label={humanizeKey(key)}
                            value={displayValue}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}

export interface BotDetailsTabProps {
    data: Record<string, unknown> | null | undefined;
}

const BotDetailsTab = ({ data }: BotDetailsTabProps) => {
    const theme = useTheme();

    if (data == null || typeof data !== "object") {
        return (
            <Box
                sx={{
                    p: 3,
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Typography variant="body1" sx={{ color: theme.palette.bg[300], fontSize: "24px" }}>
                    No bot selected
                </Typography>
            </Box>
        );
    }

    const { parameters, ...topLevel } = data as Record<string, unknown>;
    const topLevelEntries = Object.entries(topLevel).filter(
        ([k, v]) => k !== "parameters" && v !== undefined && v !== null
    );

    return (
        <Box
            role="tabpanel"
            className="vertical-tab-panel"
            sx={{
                p: 2,
                height: "100%",
                overflow: "auto",
                backgroundColor: theme.palette.bg[50],
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
            }}
        >
            <Box sx={{ width: "100%", maxWidth: 640 }}>

                {topLevelEntries.length > 0 && (
                    <Section title="Instance" data={Object.fromEntries(topLevelEntries)} />
                )}
                {isPlainObject(parameters) && Object.keys(parameters).length > 0 && (
                    <Section title="Parameters" data={parameters} />
                )}
            </Box>
        </Box>
    );
}

export default BotDetailsTab;